/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * Generates the theme object for the component from the theme and provided additional information
 * @param  {Object} theme The actual theme object.
 * @return {Object} The final theme object with the overrides and component variables
 */
const generateComponentTheme = theme => {
  var _colors$contrasts, _colors$contrasts2, _colors$contrasts3, _colors$contrasts4, _colors$contrasts5, _colors$contrasts6, _colors$contrasts7, _colors$contrasts8, _colors$contrasts9, _colors$contrasts0, _colors$contrasts1;
  const spacing = theme.spacing,
    borders = theme.borders,
    colors = theme.colors,
    forms = theme.forms,
    shadows = theme.shadows,
    typography = theme.typography,
    themeName = theme.key;
  const themeSpecificStyle = {
    canvas: {
      focusBorderColor: theme['ic-brand-primary'],
      hoverBorderColor: theme['ic-brand-font-color-dark'],
      labelColor: theme['ic-brand-font-color-dark']
    },
    'canvas-high-contrast': {
      toggleBackgroundOff: colors === null || colors === void 0 ? void 0 : (_colors$contrasts = colors.contrasts) === null || _colors$contrasts === void 0 ? void 0 : _colors$contrasts.grey125125
    }
  };
  const componentVariables = {
    labelColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts2 = colors.contrasts) === null || _colors$contrasts2 === void 0 ? void 0 : _colors$contrasts2.grey125125,
    labelFontFamily: typography === null || typography === void 0 ? void 0 : typography.fontFamily,
    labelFontWeight: typography === null || typography === void 0 ? void 0 : typography.fontWeightNormal,
    labelLineHeight: typography === null || typography === void 0 ? void 0 : typography.lineHeightCondensed,
    background: colors === null || colors === void 0 ? void 0 : (_colors$contrasts3 = colors.contrasts) === null || _colors$contrasts3 === void 0 ? void 0 : _colors$contrasts3.white1010,
    borderWidth: borders === null || borders === void 0 ? void 0 : borders.widthSmall,
    borderColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts4 = colors.contrasts) === null || _colors$contrasts4 === void 0 ? void 0 : _colors$contrasts4.grey3045,
    hoverBorderColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts5 = colors.contrasts) === null || _colors$contrasts5 === void 0 ? void 0 : _colors$contrasts5.grey125125,
    controlSize: '0.1875rem',
    focusBorderColor: colors === null || colors === void 0 ? void 0 : (_colors$contrasts6 = colors.contrasts) === null || _colors$contrasts6 === void 0 ? void 0 : _colors$contrasts6.blue4570,
    focusBorderWidth: borders === null || borders === void 0 ? void 0 : borders.widthMedium,
    focusBorderStyle: borders === null || borders === void 0 ? void 0 : borders.style,
    simpleFacadeSmallSize: '1rem',
    simpleFacadeMediumSize: '1.25rem',
    simpleFacadeLargeSize: '1.75rem',
    simpleCheckedInsetSmall: '0.1875rem',
    simpleCheckedInsetMedium: '0.25rem',
    simpleCheckedInsetLarge: '0.375rem',
    simpleFontSizeSmall: typography === null || typography === void 0 ? void 0 : typography.fontSizeSmall,
    simpleFontSizeMedium: typography === null || typography === void 0 ? void 0 : typography.fontSizeMedium,
    simpleFontSizeLarge: typography === null || typography === void 0 ? void 0 : typography.fontSizeLarge,
    simpleFacadeMarginEnd: spacing === null || spacing === void 0 ? void 0 : spacing.xSmall,
    toggleBorderRadius: borders === null || borders === void 0 ? void 0 : borders.radiusSmall,
    toggleBorderWidth: borders === null || borders === void 0 ? void 0 : borders.widthLarge,
    toggleBackgroundSuccess: colors === null || colors === void 0 ? void 0 : (_colors$contrasts7 = colors.contrasts) === null || _colors$contrasts7 === void 0 ? void 0 : _colors$contrasts7.green4570,
    toggleBackgroundOff: colors === null || colors === void 0 ? void 0 : (_colors$contrasts8 = colors.contrasts) === null || _colors$contrasts8 === void 0 ? void 0 : _colors$contrasts8.green4570,
    toggleBackgroundDanger: colors === null || colors === void 0 ? void 0 : (_colors$contrasts9 = colors.contrasts) === null || _colors$contrasts9 === void 0 ? void 0 : _colors$contrasts9.orange4570,
    toggleBackgroundWarning: colors === null || colors === void 0 ? void 0 : (_colors$contrasts0 = colors.contrasts) === null || _colors$contrasts0 === void 0 ? void 0 : _colors$contrasts0.orange4570,
    toggleHandleText: colors === null || colors === void 0 ? void 0 : (_colors$contrasts1 = colors.contrasts) === null || _colors$contrasts1 === void 0 ? void 0 : _colors$contrasts1.white1010,
    toggleSmallHeight: forms === null || forms === void 0 ? void 0 : forms.inputHeightSmall,
    toggleMediumHeight: forms === null || forms === void 0 ? void 0 : forms.inputHeightMedium,
    toggleLargeHeight: forms === null || forms === void 0 ? void 0 : forms.inputHeightLarge,
    toggleShadow: shadows === null || shadows === void 0 ? void 0 : shadows.depth1,
    // toggle font is uppercase, so sizes are smaller below
    toggleSmallFontSize: typography === null || typography === void 0 ? void 0 : typography.fontSizeXSmall,
    toggleMediumFontSize: typography === null || typography === void 0 ? void 0 : typography.fontSizeSmall,
    toggleLargeFontSize: typography === null || typography === void 0 ? void 0 : typography.fontSizeMedium
  };
  return {
    ...componentVariables,
    ...themeSpecificStyle[themeName]
  };
};
export default generateComponentTheme;